import { useGoldRates } from "@/lib/nonblocking-rates";
import { GRAMS_PER_TOLA, type Country } from "@/lib/gold-data";

// USD-relative FX approximations for the expat conversion matrix. Kept
// intentionally as static reference multipliers so the widget renders
// instantly with zero layout shift and zero extra network calls. Values are
// rounded, expat-friendly ballparks — for exact remittance quotes users are
// directed to their bank. Update range: refreshed periodically.
const FX_PER_USD: Record<string, number> = {
  SAR: 3.75, // pegged
  AED: 3.67, // pegged
  USD: 1,
  GBP: 0.79,
  BDT: 119.5,
};

const CURRENCY_META: { code: keyof typeof FX_PER_USD; label: string; flag: string; audience: string }[] = [
  { code: "SAR", label: "Saudi Riyal",     flag: "🇸🇦", audience: "KSA expats" },
  { code: "AED", label: "UAE Dirham",      flag: "🇦🇪", audience: "Dubai / UAE" },
  { code: "USD", label: "US Dollar",       flag: "🇺🇸", audience: "USA diaspora" },
  { code: "GBP", label: "British Pound",   flag: "🇬🇧", audience: "UK diaspora" },
  { code: "BDT", label: "Bangladeshi Taka",flag: "🇧🇩", audience: "Bangladesh" },
];

function fmt(n: number, code: string) {
  const digits = code === "BDT" ? 0 : 2;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: code, maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);
}

/**
 * Overseas Expat Live Conversion Matrix.
 * Converts today's 24K gold rate (per tola & per 10 g) into major diaspora
 * currencies — SAR, AED, USD, GBP, BDT — so overseas Pakistanis / NRIs can
 * cross-check the home-country rate against what they'd pay locally.
 *
 * Uses `min-h-*` to reserve vertical space and prevent CLS around it.
 */
export function OverseasMatrix({ country, cityName }: { country: Country; cityName?: string }) {
  const { data } = useGoldRates();
  const spot = data.spotUsdPerOz;
  const usdPerGram = spot / 31.1035;
  const usdPerTola = usdPerGram * GRAMS_PER_TOLA;
  const usdPer10g = usdPerGram * 10;
  const label = cityName ?? (country === "pakistan" ? "Pakistan" : "India");
  return (
    <section
      aria-label="Overseas Expat Live Conversion Matrix"
      className="mx-auto max-w-4xl px-4 pt-4"
    >
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/5 p-4 md:p-5 shadow-sm min-h-[260px] md:min-h-[220px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              🌍 Overseas Expat Live Conversion Matrix
            </div>
            <div className="text-sm text-muted-foreground">
              Today&apos;s 24K {label} gold rate in your diaspora currency — KSA, UAE, USA, UK &amp; Bangladesh.
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground">Reference FX · live spot</div>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th className="py-2 pr-3">Currency</th>
                <th className="py-2 pr-3 text-right">1 Tola (11.66 g)</th>
                <th className="py-2 pr-3 text-right">10 Grams</th>
                <th className="py-2 pr-0 text-right hidden sm:table-cell">For</th>
              </tr>
            </thead>
            <tbody>
              {CURRENCY_META.map((c) => {
                const mult = FX_PER_USD[c.code];
                return (
                  <tr key={c.code} className="border-t border-border">
                    <th scope="row" className="py-2 pr-3 text-left font-semibold whitespace-nowrap">
                      <span aria-hidden className="mr-1">{c.flag}</span>
                      {c.code} <span className="text-muted-foreground font-normal hidden md:inline">· {c.label}</span>
                    </th>
                    <td suppressHydrationWarning className="py-2 pr-3 text-right tabular-nums font-semibold">{fmt(usdPerTola * mult, c.code)}</td>
                    <td suppressHydrationWarning className="py-2 pr-3 text-right tabular-nums">{fmt(usdPer10g * mult, c.code)}</td>
                    <td className="py-2 pr-0 text-right text-xs text-muted-foreground hidden sm:table-cell">{c.audience}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Reference conversions from international spot (XAU/USD). Actual retail rate in your city may differ due to duty, VAT &amp; local premium.
        </p>
      </div>
    </section>
  );
}

/**
 * Localised text panel targeting cross-border / expat queries. Includes
 * explicit H2 / H3 headings mapping remittance intent to gold prices.
 */
export function OverseasIntent({ country, cityName }: { country: Country; cityName?: string }) {
  const cName = country === "pakistan" ? "Pakistan" : "India";
  const place = cityName ?? cName;
  const currency = country === "pakistan" ? "PKR" : "INR";
  const localTransliteration = cityName
    ? `${cityName} me aaj sone ka rate kya hai`
    : `${cName} me aaj sone ka rate kya hai`;
  const nativeHindi = cityName
    ? `आज ${cityName} में सोने का भाव`
    : `आज ${cName} में सोने का भाव`;
  return (
    <section
      aria-label={`Overseas & expat gold rate — ${place}`}
      className="mx-auto max-w-4xl px-4 py-8"
    >
      <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
        <h2 className="text-xl md:text-2xl font-bold">
          Today Gold Price in {place} for KSA &amp; UAE Expats
        </h2>
        <p className="mt-2 text-sm md:text-base text-foreground/85">
          Are you sending money home from Dubai or Saudi Arabia to buy gold in {place}?
          Track the live rate gap here — the price above is updated every 10 minutes
          from the international spot rate and today&apos;s USD/{currency} exchange.
          The Overseas Expat Live Conversion Matrix converts that same price into
          SAR, AED, USD, GBP and BDT so you can compare instantly against your local
          jeweller before you remit.
        </p>

        <h3 className="mt-5 text-lg font-semibold">{localTransliteration}</h3>
        <p className="mt-1 text-sm text-foreground/80">
          {place} me aaj 24K sona ka rate live update hota hai har 10 minute par —
          per tola aur per 10 gram dono units mein. {country === "pakistan" ? "Overseas Pakistanis" : "NRI users"}
          {" "}KSA, UAE, USA aur UK se yahin se aaj ka current bhaao check kar sakte hain
          apni local currency mein.
        </p>

        <h3 className="mt-5 text-lg font-semibold" lang="hi">{nativeHindi}</h3>
        <p className="mt-1 text-sm text-foreground/80" lang="hi">
          {place} में आज सोने का भाव — 24 कैरेट, 22 कैरेट और 18 कैरेट का लाइव रेट,
          प्रति तोला और प्रति 10 ग्राम। खाड़ी देश (गल्फ), अमेरिका और यूके से रहने
          वाले प्रवासी भी यहीं से आज का रेट देख सकते हैं।
        </p>

        <h3 className="mt-5 text-lg font-semibold">
          Gold rate in {cName} live tracking from USA / UK
        </h3>
        <p className="mt-1 text-sm text-foreground/80">
          Diaspora users in New York, London, Manchester, Toronto, Riyadh, Jeddah,
          Dubai, Abu Dhabi and Dhaka can bookmark this page for a live {cName} gold
          rate feed. All conversions above are computed client-side, so the numbers
          reflect the moment you loaded the page — refresh for a fresh quote before
          initiating a wire transfer or remittance.
        </p>
      </div>
    </section>
  );
}
